import * as React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/router';
import { SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

import { ButtonLink } from '@/components/common';
import { Button, Checkbox } from '@/components/ui';
import { useLeavePrevention } from '@/hooks/useLeavePrevention';
import { ERROR, WARN } from '@/utils/form';
import { trpc } from '@/utils/trpc';

import type { RouterOutput } from '../../../trpc/router';

type WorkoutDeleteFormValues = { confirm: boolean };

const schema: z.ZodType<WorkoutDeleteFormValues> = z.lazy(() =>
  z.object({ confirm: z.boolean({ required_error: ERROR.REQUIRED }) }),
);

export interface WorkoutDeleteFormProps {
  workout: RouterOutput['workouts']['byId'];
}

export const WorkoutDeleteForm = React.memo<WorkoutDeleteFormProps>(({ workout }) => {
  const router = useRouter();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<WorkoutDeleteFormValues>({ resolver: zodResolver(schema) });
  const watchConfirm = watch('confirm');
  const { mutateAsync } = trpc.workouts.deleteProcedure.useMutation({
    onMutate: () => toast.loading('Deleting...'),
    onSuccess: (workout) => {
      toast.remove();
      toast.success(`Workout ${workout.title} deleted`);
    },
    onError: (error) => {
      toast.remove();
      toast.error(error.message);
    },
  });

  useLeavePrevention(isDirty && !isSubmitting && !isSubmitSuccessful, WARN.UNSAVED_CHANGES);

  const onSubmit = React.useCallback<SubmitHandler<WorkoutDeleteFormValues>>(
    ({ confirm }) => confirm && mutateAsync({ id: workout.data.id }).then(() => router.push(`/admin/workouts`)),
    [mutateAsync, router, workout.data.id],
  );

  return (
    <form className='bg-khaki-1 rounded-lg p-8 overflow-hidden' onSubmit={handleSubmit(onSubmit)}>
      <h2 className='font-josefin font-bold text-2xl tracking-tighter'>
        Are you sure you want to permanently delete {workout.data.title}?
      </h2>
      <Checkbox errors={errors} label='I understand this action can not be undone.' {...register('confirm')} />
      <div className='flex gap-x-6 mt-2 -mx-8 -mb-8 p-8 py-5 rounded-bl-lg bg-khaki-2-3'>
        <Button
          disabled={isSubmitting || !watchConfirm}
          state={isSubmitting ? 'waiting' : undefined}
          type='submit'
          variant='danger'
        >
          Yes, delete this workout
        </Button>
        <ButtonLink href={`/admin/workouts/${workout.data.id}`} variant='minimal'>
          No, I changed my mind
        </ButtonLink>
      </div>
    </form>
  );
});

WorkoutDeleteForm.displayName = 'WorkoutDeleteForm';
